import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Grid, Paper, Button, IconButton,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, CircularProgress, Tooltip, Menu, MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Folder as FolderIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  AutoAwesome as AiIcon,
  Description as DocIcon,
  ArrowForward as ArrowIcon,
  WorkspacePremium as WorkIcon,
} from '@mui/icons-material';
import axios from 'axios';
import AuthContext from '../contexts/AuthContext';

const COLORI = [
  '#1B2A4A', '#2563EB', '#7C3AED', '#059669', '#DC2626',
  '#D97706', '#0891B2', '#BE185D', '#4B5563', '#9333EA'
];

interface Fascicolo {
  id: string;
  nome: string;
  colore: string;
  appunti: string;
  createdAt: string;
  updatedAt: string;
  _count: { documenti: number; aiChats: number };
}

const BookyWorkPage: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [fascicoli, setFascicoli] = useState<Fascicolo[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nuovoNome, setNuovoNome] = useState('');
  const [nuovoColore, setNuovoColore] = useState(COLORI[0]);
  const [creando, setCreando] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [fascicoloSelezionato, setFascicoloSelezionato] = useState<string | null>(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { caricaFascicoli(); }, []);

  const caricaFascicoli = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/fascicoli', { headers });
      setFascicoli(res.data.fascicoli);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const creaFascicolo = async () => {
    if (!nuovoNome.trim()) return;
    try {
      setCreando(true);
      const res = await axios.post('/api/fascicoli', { nome: nuovoNome, colore: nuovoColore }, { headers });
      setFascicoli([res.data.fascicolo, ...fascicoli]);
      setDialogOpen(false);
      setNuovoNome('');
      setNuovoColore(COLORI[0]);
    } catch (e) {
      console.error(e);
    } finally {
      setCreando(false);
    }
  };

  const eliminaFascicolo = async (id: string) => {
    try {
      await axios.delete(`/api/fascicoli/${id}`, { headers });
      setFascicoli(fascicoli.filter(f => f.id !== id));
    } catch (e) {
      console.error(e);
    }
    setMenuAnchor(null);
  };

  const formatData = (d: string) =>
    new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2,
            background: 'linear-gradient(135deg, #1B2A4A, #2563EB)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <WorkIcon sx={{ color: 'white', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ fontFamily: 'Syne, sans-serif', lineHeight: 1.1 }}>
              BOOKY Work
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Il tuo spazio di lavoro personale
            </Typography>
          </Box>
        </Box>

        {/* ── SPIEGAZIONE ONBOARDING (sempre visibile) ── */}
        <Paper elevation={0} sx={{
          p: 3, mt: 3,
          background: 'linear-gradient(135deg, rgba(37,99,235,0.05) 0%, rgba(124,58,237,0.05) 100%)',
          border: '1px solid rgba(37,99,235,0.15)',
          borderRadius: 3
        }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: '#1B2A4A' }}>
            Come funziona BOOKY Work
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                num: '1',
                titolo: 'Crea un fascicolo',
                testo: 'Ogni fascicolo rappresenta una causa o un cliente. Dai un nome e scegli un colore per riconoscerlo subito.',
                colore: '#2563EB'
              },
              {
                num: '2',
                titolo: 'Cerca e raccogli',
                testo: 'Dentro il fascicolo trovi BOOKY Search già integrato. Cerca le sentenze che ti servono e aggiungile al caso con un click. Puoi aggiungere una nota per ogni sentenza.',
                colore: '#7C3AED'
              },
              {
                num: '3',
                titolo: 'Scrivi i tuoi appunti',
                testo: 'Ogni fascicolo ha uno spazio per i tuoi appunti liberi: strategie, memorie, cose da fare. Si salva in automatico.',
                colore: '#059669'
              },
              {
                num: '4',
                titolo: 'Interroga l\'AI del caso',
                testo: 'Fai domande all\'AI che ragiona solo sulle sentenze che hai raccolto. Tutte le conversazioni vengono salvate nel fascicolo.',
                colore: '#D97706'
              }
            ].map(({ num, titolo, testo, colore }) => (
              <Grid item xs={12} sm={6} md={3} key={num}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Box sx={{
                    minWidth: 28, height: 28, borderRadius: '50%',
                    bgcolor: colore, color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 800, mt: 0.2
                  }}>
                    {num}
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.4 }}>{titolo}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, fontSize: 13 }}>
                      {testo}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>

      {/* ── AZIONI ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={700}>
          I tuoi fascicoli {fascicoli.length > 0 && <Chip label={fascicoli.length} size="small" sx={{ ml: 1, fontWeight: 700 }} />}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{
            fontWeight: 700, borderRadius: 2, textTransform: 'none',
            background: 'linear-gradient(135deg, #1B2A4A, #2563EB)',
            '&:hover': { background: 'linear-gradient(135deg, #0d1a2e, #1d4ed8)' }
          }}
        >
          Nuovo fascicolo
        </Button>
      </Box>

      {/* ── LISTA FASCICOLI ────────────────────────────────────────────────── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : fascicoli.length === 0 ? (
        <Paper elevation={0} sx={{
          p: 6, textAlign: 'center',
          border: '2px dashed rgba(37,99,235,0.2)', borderRadius: 3,
          background: 'rgba(37,99,235,0.02)'
        }}>
          <FolderIcon sx={{ fontSize: 56, color: 'rgba(37,99,235,0.3)', mb: 2 }} />
          <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
            Nessun fascicolo ancora
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
            Crea il tuo primo fascicolo per iniziare a organizzare il lavoro per causa o cliente.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{ fontWeight: 700, borderRadius: 2, textTransform: 'none' }}
          >
            Crea il primo fascicolo
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {fascicoli.map((f) => (
            <Grid item xs={12} sm={6} md={4} key={f.id}>
              <Paper
                elevation={0}
                sx={{
                  p: 0, borderRadius: 3, overflow: 'hidden',
                  border: '1px solid rgba(0,0,0,0.08)',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    transform: 'translateY(-3px)',
                    borderColor: f.colore
                  }
                }}
                onClick={() => navigate(`/booky-work/${f.id}`)}
              >
                {/* Banda colorata in cima */}
                <Box sx={{ height: 6, bgcolor: f.colore }} />

                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                      <Box sx={{
                        width: 40, height: 40, borderRadius: 2,
                        bgcolor: f.colore + '20', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', flexShrink: 0
                      }}>
                        <FolderIcon sx={{ color: f.colore, fontSize: 22 }} />
                      </Box>
                      <Typography
                        variant="subtitle1" fontWeight={700}
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {f.nome}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFascicoloSelezionato(f.id);
                        setMenuAnchor(e.currentTarget);
                      }}
                      sx={{ ml: 1, flexShrink: 0 }}
                    >
                      <MoreIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Statistiche */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Tooltip title="Sentenze nel fascicolo">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DocIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          {f._count.documenti} {f._count.documenti === 1 ? 'sentenza' : 'sentenze'}
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Tooltip title="Conversazioni AI salvate">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AiIcon sx={{ fontSize: 16, color: '#7C3AED' }} />
                        <Typography variant="body2" sx={{ color: '#7C3AED' }} fontWeight={600}>
                          {f._count.aiChats} AI
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>

                  {f.appunti && (
                    <Typography
                      variant="body2" color="text.secondary"
                      sx={{
                        mb: 2, fontSize: 13, lineHeight: 1.5,
                        overflow: 'hidden', display: '-webkit-box',
                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {f.appunti}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.disabled">
                      Aggiornato {formatData(f.updatedAt)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: f.colore }}>
                      <Typography variant="body2" fontWeight={700} fontSize={13}>Apri</Typography>
                      <ArrowIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── MENU CONTESTUALE ───────────────────────────────────────────────── */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 160 } }}
      >
        <MenuItem
          onClick={() => {
            if (fascicoloSelezionato) eliminaFascicolo(fascicoloSelezionato);
          }}
          sx={{ color: 'error.main', gap: 1.5 }}
        >
          <DeleteIcon fontSize="small" />
          Elimina fascicolo
        </MenuItem>
      </Menu>

      {/* ── DIALOG NUOVO FASCICOLO ──────────────────────────────────────────── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontFamily: 'Syne, sans-serif' }}>
          Nuovo fascicolo
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Dai un nome al tuo fascicolo — di solito il nome del cliente o della causa.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Nome del fascicolo"
            placeholder="Es. Causa Rossi vs Comune di Milano"
            value={nuovoNome}
            onChange={e => setNuovoNome(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && creaFascicolo()}
            sx={{ mb: 3 }}
          />
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
            Colore del fascicolo
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {COLORI.map(c => (
              <Box
                key={c}
                onClick={() => setNuovoColore(c)}
                sx={{
                  width: 32, height: 32, borderRadius: '50%', bgcolor: c,
                  cursor: 'pointer',
                  border: nuovoColore === c ? '3px solid #000' : '3px solid transparent',
                  boxShadow: nuovoColore === c ? '0 0 0 2px white inset' : 'none',
                  transition: 'all 0.15s',
                  '&:hover': { transform: 'scale(1.15)' }
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={creaFascicolo}
            disabled={!nuovoNome.trim() || creando}
            startIcon={creando ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{
              fontWeight: 700, textTransform: 'none', borderRadius: 2,
              bgcolor: nuovoColore, '&:hover': { bgcolor: nuovoColore, filter: 'brightness(0.85)' }
            }}
          >
            Crea fascicolo
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default BookyWorkPage;
